import React, { useCallback } from 'react'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Group from 'components/utils/Group'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import moment from 'moment/moment'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import { ITEM_STATUS_AWAITING_DATES } from 'constants/cart'
import { canChangeHotelDates, canChangeHotelRooms, getHotelItems, canCancelOrderItem, getFreeCancellationPeriodEndDate, canExtendHotelDates, canRequestChangeHotelDates } from 'supportAssistant/utils/orderUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const ImageWrapper = styled.div`
  width: 100%;
  ${mediaQueryUp.tablet} {
    width: ${rem(100)};
  }
`

interface Props {
  item?: any;
  order?: any;
  offer?: any;
  roomLabel: string;
  hideSelectButton?: boolean;
  isCurrentOrder?: boolean;
}

export default function LesaHotelItemSummary(props: Props) {
  const { item, order, offer, roomLabel, hideSelectButton = false, isCurrentOrder } = props

  const isFlashExtendDatesEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.flashExtendDatesEnabled)
  const isOutsidePolicyChangeDatesRequestEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.csxOutsidePolicyChangeDatesRequestEnabled)

  const dispatch = useAppDispatch()

  const items = getHotelItems(order)
  const bookingNumber = item.bookingNumber
  const canBeCancelled = canCancelOrderItem(order, item)
  const freeCancellationPeriodEndDate = getFreeCancellationPeriodEndDate(order, item)
  const canChangeDates = canChangeHotelDates(order, offer)
  const canRequestChangeDates = isOutsidePolicyChangeDatesRequestEnabled && canRequestChangeHotelDates(order, offer)
  const canChangeRooms = canChangeHotelRooms(order, offer, items)
  const canExtendDates = isFlashExtendDatesEnabled && canChangeDates && canExtendHotelDates(order, offer, item.id)
  const awaitingDates = item.status === ITEM_STATUS_AWAITING_DATES
  const hasFlight = !!order.hasFlight

  const onSelect = useCallback(() => {
    dispatch(addChatMessage(item.bookingNumber, {
      action: 'selectOrderItem',
      payload: {
        awaitingDates,
        bookingNumber,
        canBeCancelled,
        freeCancellationPeriodEndDate,
        canChangeDates,
        canRequestChangeDates,
        canChangeRooms,
        canExtendDates,
        checkInDate: item.reservation?.startDate,
        hasFlight,
        id_orders: order.id,
        itemId: item.id,
        orderType: 'hotel',
        isCurrentOrder,
      },
    }))
  }, [awaitingDates, bookingNumber, canBeCancelled, canChangeDates, canChangeRooms, canExtendDates, canRequestChangeDates, dispatch, freeCancellationPeriodEndDate, hasFlight, isCurrentOrder, item.bookingNumber, item.id, item.reservation?.startDate, order.id])

  if (!item) {
    return null
  }

  return <LesaPurchaseSummaryContainer>
    <LesaPurchaseSummaryHeader>
      <StructureLabel variant="standard">{roomLabel}</StructureLabel>
    </LesaPurchaseSummaryHeader>
    <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">{item.package.name}</BodyText>
        <BodyText variant="medium">{item.package.roomTypeName}</BodyText>
        {bookingNumber && <BodyText variant="small" colour="neutral-two"><strong>Booking ID:</strong> {bookingNumber}</BodyText>}
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineUsersAltIcon colour="primary" size="XS" />
          <BodyText variant="medium" format="titlecase">
            {formatOccupantsShort(item.reservation)}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineCalendarIcon size="XS" />
          <BodyText variant="medium">{moment(item.reservation?.startDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}</BodyText>
          <LineArrowRightIcon colour="neutral-four" size="XS" />
          <BodyText variant="medium">{moment(item.reservation?.endDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}</BodyText>
        </Group>
      </Group>
      <Group gap={16} direction="vertical">
        <ImageWrapper>
          <ImageWithReservedSpace id={item.package.roomTypeImage ?? item?.offer.image.id} />
        </ImageWrapper>
        {!hideSelectButton && <TextButton onClick={onSelect} fit="mobile-full-width" size="small" kind="secondary">Select</TextButton>}
      </Group>
    </Group>
  </LesaPurchaseSummaryContainer>
}
